import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EdiFileProcessTable from '../Component/EdiFileProcessManagement/EdiFileProcessTable';
import { inputStyle } from '../Component/OrderEntry/styles';
import { tokens, withAlpha } from '../Common/designTokens';
import { EDI_FILE_PROCESS_FILTER_PORTAL_ID } from '../Constants/AppConstants';
import { useAuth } from '../Context/AuthProvider';
import { fetchEdiFileProcessDetails, retryEdiFileProcess } from '../http/api';
import { useEdiFileProcessStore } from '../Store/ediFileProcessStore';
import { localToUtcDateTimeParam } from '../Utils/timeConversions';
import useSnackbar from '../Utils/useSnackbar';
import { usePageSubscription } from '../Utils/WebSocketProvider.jsx';

const snakeToCamel = (key) =>
  key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

const normalizeKeysToCamelCase = (row = {}) =>
  Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[snakeToCamel(key)] = value;
    return acc;
  }, {});

const normalizeEdiFileProcessRows = (rows = []) =>
  rows.map((row) => normalizeKeysToCamelCase(row));

const EMPTY_DATE_TIME_FILTERS = {
  start: null,
  end: null,
};

const dateTimePickerStyle = {
  ...inputStyle,
  width: { xs: '100%', sm: '215px' },
  '& .MuiFilledInput-root': {
    ...inputStyle['& .MuiFilledInput-root'],
    height: '38px',
    minHeight: '38px',
    paddingTop: '6px',
    paddingRight: '2px',
  },
  '& .MuiFilledInput-input': {
    color: tokens.colors.fontColour,
    fontSize: '14px',
    lineHeight: 1.2,
    padding: '12px 0 2px 10px !important',
    minWidth: 0,
  },
  '& .MuiInputLabel-root': {
    ...inputStyle['& .MuiInputLabel-root'],
    fontSize: '13.5px',
    lineHeight: 1,
    color: tokens.colors.fontColour85,
    transform: 'translate(10px, 4px) scale(0.72)',
    pointerEvents: 'none',
    '&.Mui-focused': {
      color: tokens.palette.primary,
      transform: 'translate(10px, 4px) scale(0.72)',
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(10px, 4px) scale(0.72)',
    },
  },
  '& .MuiInputAdornment-root': {
    marginLeft: '-38px',
  },
  '& .MuiIconButton-root': {
    padding: '4px',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
};

const filterIconButtonStyle = {
  width: 36,
  height: 36,
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: '6px',
  backgroundColor: tokens.colors.white,
  color: tokens.colors.fontColour80,
  '&:hover': {
    borderColor: tokens.palette.primaryA25,
    backgroundColor: tokens.colors.defaultGridHoverColor,
  },
  '&.Mui-disabled': {
    backgroundColor: tokens.colors.defaultGridRowColor,
    color: tokens.colors.disabledFontColour,
    borderColor: tokens.colors.bodyBorderColor50,
  },
};

const isValidDateTimeValue = (value) =>
  !value || (typeof value.isValid === 'function' && value.isValid());

const hasDateTimeFilterValue = (filters) => Boolean(filters?.start || filters?.end);

const hasInvalidDateTimeFilters = (filters) => {
  const start = filters?.start;
  const end = filters?.end;

  if (!isValidDateTimeValue(start) || !isValidDateTimeValue(end)) return true;
  return Boolean(start && end && start.isAfter(end));
};

const getEdiDateTimeQueryParams = (filters) => {
  const processingStartDateTime = localToUtcDateTimeParam(filters?.start);
  const processingEndDateTime = localToUtcDateTimeParam(filters?.end);

  return {
    ...(processingStartDateTime ? { processingStartDateTime } : {}),
    ...(processingEndDateTime ? { processingEndDateTime } : {}),
  };
};

const getPageData = (response) => {
  const data = response?.data || response || {};
  const content = Array.isArray(data?.content)
    ? data.content
    : Array.isArray(data)
      ? data
      : [];

  return {
    content,
    totalElements: data?.totalElements ?? content.length,
  };
};

const formatRetryDateFromFileName = (fileName) => {
  const match = String(fileName || '').trim().match(/^(\d{8})/);
  if (!match) return null;

  const rawDate = match[1];
  const year = Number(rawDate.slice(0, 4));
  const month = Number(rawDate.slice(4, 6));
  const day = Number(rawDate.slice(6, 8));
  const parsedDate = new Date(Date.UTC(year, month - 1, day));

  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return null;
  }

  return `${rawDate.slice(0, 4)}-${rawDate.slice(4, 6)}-${rawDate.slice(6, 8)}`;
};

const EdiFileProcessManagement = ({ embedded = false }) => {
  const { token } = useAuth();
  usePageSubscription('ediFileProcess');
  const { showError, showSuccess, SnackbarComponent } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [retryingId, setRetryingId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'processingDateTime', direction: 'desc' });
  const [dateTimeFilters, setDateTimeFilters] = useState(EMPTY_DATE_TIME_FILTERS);
  const [appliedDateTimeFilters, setAppliedDateTimeFilters] = useState(EMPTY_DATE_TIME_FILTERS);
  const [filterPortalElement, setFilterPortalElement] = useState(null);

  const {
    ediFileProcessData,
    totalElements,
    loading,
    error,
    setEdiFileProcessData,
    setTotalElements,
    setLoading,
    setError,
  } = useEdiFileProcessStore();

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetchEdiFileProcessDetails(
        token,
        page,
        rowsPerPage,
        sortConfig.key,
        sortConfig.direction,
        getEdiDateTimeQueryParams(appliedDateTimeFilters)
      );
      const pageData = getPageData(response);
      setEdiFileProcessData(normalizeEdiFileProcessRows(pageData.content));
      setTotalElements(pageData.totalElements);
    } catch (fetchError) {
      console.error('Error fetching EDI file processing details:', fetchError);
      setError(fetchError.message || 'Failed to load EDI file processing details');
      setEdiFileProcessData([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    page,
    rowsPerPage,
    sortConfig,
    appliedDateTimeFilters,
    setEdiFileProcessData,
    setError,
    setLoading,
    setTotalElements,
  ]);

  const handleDateTimeFilterChange = (key, value) => {
    setDateTimeFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
  };

  const handleApplyDateTimeFilter = () => {
    if (hasInvalidDateTimeFilters(dateTimeFilters)) {
      showError('Please choose a valid processing date time range.');
      return;
    }

    setPage(0);
    setAppliedDateTimeFilters({ ...dateTimeFilters });
  };

  const handleClearDateTimeFilter = () => {
    setDateTimeFilters(EMPTY_DATE_TIME_FILTERS);
    setAppliedDateTimeFilters(EMPTY_DATE_TIME_FILTERS);
    setPage(0);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!embedded) return;

    setFilterPortalElement(document.getElementById(EDI_FILE_PROCESS_FILTER_PORTAL_ID));

    return () => {
      setFilterPortalElement(null);
    };
  }, [embedded]);

  const handleSort = (columnKey, direction) => {
    if (columnKey === 'duration' || columnKey === 'actionEdiFileProcess') return;

    setPage(0);
    setSortConfig((currentConfig) => ({
      key: columnKey,
      direction:
        direction ||
        (currentConfig.key === columnKey && currentConfig.direction === 'asc'
          ? 'desc'
          : 'asc'),
    }));
  };

  const handleRetryClick = async (rowData) => {
    if (!token || rowData?.id == null) return;

    const retryDate = formatRetryDateFromFileName(rowData?.fileName);
    if (!retryDate) {
      showError('Unable to retry: file name must begin with a valid date in YYYYMMDD format.');
      return;
    }

    setRetryingId(rowData.id);

    try {
      const response = await retryEdiFileProcess(token, {
        edi_file_processed_id: rowData.id,
        date: retryDate,
      });

      if (response?.success === true) {
        showSuccess('Retry triggered successfully. The EDI process may take some time to complete.');
        if (page === 0) {
          await fetchData();
        } else {
          setPage(0);
        }
      } else {
        showError(response?.message || 'Failed to trigger EDI file process retry');
      }
    } catch (retryError) {
      console.error('Error triggering EDI file process retry:', retryError);
      showError(retryError?.message || 'Failed to trigger EDI file process retry');
    } finally {
      setRetryingId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hasDraftDateTimeFilter = hasDateTimeFilterValue(dateTimeFilters);
  const hasActiveDateTimeFilter = hasDateTimeFilterValue(appliedDateTimeFilters);
  const isDateTimeFilterInvalid = hasInvalidDateTimeFilters(dateTimeFilters);
  const dateTimeFilterControls = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <DateTimePicker
        label="Start Date Time"
        value={dateTimeFilters.start}
        onChange={(value) => handleDateTimeFilterChange('start', value)}
        maxDateTime={dateTimeFilters.end || undefined}
        ampm={false}
        views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
        format="YYYY-MM-DD HH:mm:ss"
        slotProps={{
          textField: {
            variant: 'filled',
            sx: dateTimePickerStyle,
            InputProps: { disableUnderline: true },
            InputLabelProps: { shrink: true },
            placeholder: 'YYYY-MM-DD HH:mm:ss',
            error: !isValidDateTimeValue(dateTimeFilters.start),
          },
        }}
      />
      <DateTimePicker
        label="End Date Time"
        value={dateTimeFilters.end}
        onChange={(value) => handleDateTimeFilterChange('end', value)}
        minDateTime={dateTimeFilters.start || undefined}
        ampm={false}
        views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
        format="YYYY-MM-DD HH:mm:ss"
        slotProps={{
          textField: {
            variant: 'filled',
            sx: dateTimePickerStyle,
            InputProps: { disableUnderline: true },
            InputLabelProps: { shrink: true },
            placeholder: 'YYYY-MM-DD HH:mm:ss',
            error: !isValidDateTimeValue(dateTimeFilters.end),
          },
        }}
      />
      <Button
        variant="contained"
        onClick={handleApplyDateTimeFilter}
        disabled={loading || isDateTimeFilterInvalid}
        sx={{
          height: 36,
          minWidth: 64,
          px: 1.5,
          textTransform: 'none',
          fontSize: '12px',
          fontWeight: 700,
          boxShadow: 'none',
          borderRadius: '6px',
          backgroundColor: tokens.palette.primary,
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: tokens.palette.primaryA90,
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.disabledFontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          },
        }}
      >
        Apply
      </Button>
      <Tooltip title="Clear processing date time filter">
        <span>
          <IconButton
            onClick={handleClearDateTimeFilter}
            disabled={loading || (!hasDraftDateTimeFilter && !hasActiveDateTimeFilter)}
            sx={filterIconButtonStyle}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {embedded && filterPortalElement
        ? createPortal(dateTimeFilterControls, filterPortalElement)
        : null}
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: tokens.colors.bodyBackgroundColor,
        }}
      >
        {!embedded && (
          <Box sx={{ color: tokens.colors.fontColour, padding: '16px', paddingY: '14px', paddingBottom: '0px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: `1px solid ${withAlpha(tokens.colors.gray, 0.2)}`,
                minHeight: '48px',
                height: 'auto',
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                EDI File Processing
              </Typography>
              {dateTimeFilterControls}
            </Box>
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: 'hidden', padding: embedded ? '0px' : '16px' }}>
          <EdiFileProcessTable
            data={ediFileProcessData}
            setData={setEdiFileProcessData}
            sortConfig={sortConfig}
            onSort={handleSort}
            error={error}
            loading={loading}
            onRetryClick={handleRetryClick}
            retryingId={retryingId}
            page={page}
            rowsPerPage={rowsPerPage}
            totalElements={totalElements}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            tableHeight={embedded ? 'calc(100vh - 225px)' : undefined}
          />
        </Box>
        <SnackbarComponent />
      </Box>
    </LocalizationProvider>
  );
};

export default EdiFileProcessManagement;
